import type { SipContextType } from '@/context/SipContext';

/**
 * Initiates a phone call through the SIP context
 * @param number - Phone number to call (must be digits only, optional + prefix)
 * @param sipContext - SIP context containing call management functions
 * @param notify - Optional callback to display error messages to the user
 */
export const makeCall = async (
  number: string,
  sipContext: SipContextType,
  notify?: (message: string) => void,
): Promise<void> => {
  // Validate phone number format
  if (!number.match(/^\+?[0-9]+$/)) {
    console.warn('Invalid phone number format:', number);
    notify?.('Invalid phone number format. Use only digits and optional + prefix.');
    return;
  }

  try {
    // Initialize audio on first call attempt (requests microphone permissions)
    if (sipContext.initializeAudio) {
      await sipContext.initializeAudio();
    }

    // Make the call through the SIP context
    if (sipContext.makeCall) {
      sipContext.makeCall(number);
    } else {
      throw new Error('makeCall function not available in SIP context');
    }
  } catch (error) {
    console.error('Failed to initialize audio or make call:', error);

    // Handle specific microphone/audio errors with user-friendly messages
    if (notify) {
      if (error instanceof Error) {
        switch (error.name) {
          case 'NotAllowedError':
            notify('Microphone permission denied. Please allow microphone access and try again.');
            break;
          case 'NotFoundError':
            notify('No microphone found. Please connect a microphone and try again.');
            break;
          case 'NotReadableError':
            notify('Microphone is in use by another application. Please close other apps and try again.');
            break;
          default:
            notify(`Call failed: ${error.message}`);
        }
      } else {
        notify('Failed to make call. Please check your microphone permissions.');
      }
    }
  }
};
